import { Injectable } from '@angular/core';

import { ApiService } from '@dav-core/services/api.service';
import { RemitService } from '@dav-core/services/remit.service';
import {
  PendingContractResponse,
  RemittedContractResponse,
} from '@dav-model/api/response/pending-contract.response.model';
import { UserResponse } from '@dav-model/login/response/user.response.model';
import { selectUser } from '@dav-store/user/user.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import * as RemitActions from './remit.actions';
import * as remitReducer from './remit.reducer';
import * as fromRoot from '../reducers';
import { UpdatePendingNumberAction } from '../user/user.actions';

@Injectable()
export class RemitEffects {
  constructor(private actions$: Actions, private apiService: ApiService, private store: Store<fromRoot.State>) {}

  getPendingContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.GET_PENDING_CONTRACTS),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select(selectUser)
      ),
      mergeMap(([, remitState, user]: [RemitActions.GetPendingContractsAction, remitReducer.State, UserResponse]) => {
        const allDealers = JSON.parse(JSON.stringify(user.allDealers));

        if (allDealers && allDealers.length === 1) {
          allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
        }

        const request = {
          cmsdealernumber: user.cmsDealerNumber,
          pendingToRemitContracts: remitState.pendingToRemitContracts,
          pendingToDeclineContracts: remitState.pendingToDeclineContracts,
          programId: user.programId,
          allDealers: allDealers,
          pendingDays: user.pendingDays,
        };

        return this.apiService.getPendingContracts(request).pipe(
          // If successful, dispatch success action with result
          map((response: PendingContractResponse) => {
            this.store.dispatch(new UpdatePendingNumberAction(response.pendingContracts.length));
            const pendingFilters = RemitService.fetchContractGroups(response.pendingContracts);

            return {
              type: RemitActions.GET_PENDING_CONTRACTS_SUCCESS,
              payload: { contracts: response, filters: pendingFilters },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: RemitActions.REMIT_FAILURE,
              payload: error,
            })
          )
        );
      })
    )
  );

  submitPendingContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.SUBMIT_ALL_PENDING_REMIT),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select((state) => state.userState.user)
      ),
      switchMap(
        ([action, remitState, user]: [RemitActions.GetPendingContractsAction, remitReducer.State, UserResponse]) => {
          const request = {
            cmsdealernumber: user.cmsDealerNumber,
            pendingToRemitContracts: remitState.pendingToRemitContracts,
            pendingToDeclineContracts: remitState.pendingToDeclineContracts,
            programId: user.programId,
            allDealers: user.allDealers,
            pendingDays: user.pendingDays,
          };

          return this.apiService.submitPendingContracts(request).pipe(
            // If successful, dispatch success action with result
            map((response: PendingContractResponse) => {
              const pendingFilters = RemitService.fetchContractGroups(response.pendingContracts);
              this.store.dispatch(new UpdatePendingNumberAction(response.pendingContracts.length));
              response = RemitService.resetContracts(response);

              return {
                type: RemitActions.SUBMIT_ALL_PENDING_REMIT_SUCCESS,
                payload: {
                  submittedRemittedRequest: remitState.pendingToRemitContracts,
                  contracts: response,
                  filters: pendingFilters,
                },
              };
            }),
            // If request fails, dispatch failed action
            catchError((error) => {
              return of({
                type: RemitActions.REMIT_FAILURE,
                payload: error,
              });
            })
          );
        }
      )
    )
  );

  getRemittedContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RemitActions.GET_REMITTED_CONTRACTS),
      withLatestFrom(
        this.store.select((state) => state.remitState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(([, , user]: [RemitActions.GetRemittedContractsAction, remitReducer.State, UserResponse]) => {
        const request = {
          cmsdealernumber: user.cmsDealerNumber,
          programId: user.programId,
          allDealers: user.allDealers,
        };

        return this.apiService.getRemittedContracts(request).pipe(
          // If successful, dispatch success action with result
          map((response: RemittedContractResponse) => {
            const pendingFilters = RemitService.fetchRemittedContractGroups(response.remittedContracts);

            return {
              type: RemitActions.GET_REMITTED_CONTRACTS_SUCCESS,
              payload: { contracts: response, filters: pendingFilters },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: RemitActions.REMIT_FAILURE,
              payload: error,
            })
          )
        );
      })
    )
  );
}
